import { QUARTERS_PER_HOUR } from '../shared/hours';
import { Money } from '../shared/money';

/**
 * Custo da mão de obra.
 *
 * O funcionário é contratado por **salário mensal** — é isso que a folha paga e o que o
 * escritório sabe informar. O custo por hora não é um segundo cadastro: é uma **conta**,
 * e o sistema a faz sozinho (RN-059):
 *
 * ```text
 * custo/hora = salário do mês × (1 + encargos) ÷ horas úteis do mês
 * ```
 *
 * Os encargos entraram na Fase 29 (D12): um % por funcionário, e a conta vale em todo lugar.
 *
 * "Horas úteis do mês" sai do calendário de trabalho, não de uma constante. Isso tem uma
 * consequência que **é o comportamento correto**, ainda que surpreenda à primeira vista:
 * o custo/hora **varia de mês para mês**. Dezembro, com Natal e feriados, tem menos horas
 * úteis que agosto — e a mesma folha diluída em menos horas custa mais por hora. Fixar
 * um divisor médio esconderia isso e faria o custo da obra fechar errado exatamente nos
 * meses em que a diferença importa.
 *
 * Salário ausente continua ausente: "custo indisponível" e "custa zero" são coisas
 * diferentes (RN-055), e nenhuma conta transforma uma na outra.
 */

export interface HourlyRate {
  readonly perHour: Money;
  /** Divisor usado — a tela mostra para que o número não pareça mágico. */
  readonly monthlyQuarterHours: number;
}

/**
 * Custo por hora a partir do salário do mês.
 *
 * Devolve `undefined` quando não há salário **ou** quando o mês não tem hora útil
 * nenhuma: dividir por zero produziria `Infinity`, que a tela exibiria como um número.
 */
export function hourlyRateFromMonthlySalary(
  monthlySalary: Money | undefined,
  monthlyQuarterHours: number,
): HourlyRate | undefined {
  if (monthlySalary === undefined) return undefined;
  if (monthlyQuarterHours <= 0) return undefined;

  const hours = monthlyQuarterHours / QUARTERS_PER_HOUR;
  const perHour = Money.fromCents(Math.round(monthlySalary.cents / hours));
  /* c8 ignore next -- o arredondamento sempre produz inteiro */
  if (!perHour.ok) return undefined;

  return { perHour: perHour.value, monthlyQuarterHours };
}

/**
 * O custo do mês com os encargos (Fase 29, D12): salário × (1 + encargos).
 *
 * Em centavos, arredondado no fim — a mesma conta da folha. Sem salário não há custo
 * (RN-055); sem encargo informado, é o salário puro.
 */
export function monthlyCostWithCharges(
  monthlySalary: Money | undefined,
  chargesPercent: number | undefined,
): Money | undefined {
  if (monthlySalary === undefined) return undefined;
  if (chargesPercent === undefined || chargesPercent === 0) return monthlySalary;
  const comEncargo = Money.fromCents(Math.round(monthlySalary.cents * (1 + chargesPercent / 100)));
  /* c8 ignore next -- o arredondamento sempre produz inteiro, e o encargo nunca é negativo */
  return comEncargo.ok ? comEncargo.value : undefined;
}

/**
 * O custo por hora **do funcionário**: o do mês com encargos, ÷ as horas úteis do mês.
 *
 * É a conta que vale em todo lugar desde a Fase 29 (D12) — no painel, na tela de mão de obra e
 * no orçamento. Ter duas contas, uma com encargo e outra sem, faria o orçado e o realizado
 * custarem diferente pela mesma hora.
 */
export function employeeHourlyRate(
  employee: { readonly monthlySalary?: Money; readonly chargesPercent?: number },
  monthlyQuarterHours: number,
): HourlyRate | undefined {
  return hourlyRateFromMonthlySalary(
    monthlyCostWithCharges(employee.monthlySalary, employee.chargesPercent),
    monthlyQuarterHours,
  );
}

/**
 * Custo de um número de horas, ao custo/hora do mês.
 *
 * Multiplica **em centavos**: `R$ 12,34/h × 27h` fecha no centavo, e não em
 * `333,18000000000006`.
 */
export function costOfQuarterHours(
  rate: HourlyRate | undefined,
  quarterHours: number,
): Money | undefined {
  if (rate === undefined) return undefined;
  return rate.perHour.times(quarterHours / QUARTERS_PER_HOUR);
}
